import type { ScriptSessionView } from '@api/devices/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, TimerReset } from 'lucide-react';
import { approveScriptSession, endScriptSession, getScriptSession, isStatus, openScriptSession } from '../lib/api';
import { describeKeyError, signWithKey } from '../lib/webauthn';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import { useNow } from '../ui/misc';
import { useToast } from '../ui/toast';

// PowerShell sessions (WRMM-82, Harry 2026-10-09): one key touch approves your scripts on one device for 15 minutes,
// instead of a touch per script. Each script is still its own job, recorded with the session that approved it.

export function useScriptSession(deviceId: string) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'script-session', deviceId], queryFn: ({ signal }) => getScriptSession(mspId, deviceId, signal),
    refetchInterval: q => (q.state.data ? 15_000 : false) });
}

/** The session's time left, or null once it has ended or run out. */
export function timeLeft(session: ScriptSessionView | null | undefined, now: number) {
  const ms = session && !session.endedAt ? Date.parse(session.expiresAt) - now : 0;
  if (ms <= 0) return null;
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Open (with the reason typed for the script) or end the person's PowerShell session on this device. */
export function ScriptSessionBar({ deviceId, reason, canApprove }: { deviceId: string; reason: string; canApprove: boolean }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const session = useScriptSession(deviceId);
  const now = useNow(1000);
  const left = timeLeft(session.data, now);
  const refresh = () => void client.invalidateQueries({ queryKey: ['scope', mspId, 'script-session', deviceId] });
  const open = useMutation({
    mutationFn: async () => {
      const { session: asked, approval } = await openScriptSession(mspId, deviceId, reason.trim());
      return approveScriptSession(mspId, asked.id, await signWithKey(approval));
    },
    onSuccess: () => { refresh(); toast({ title: 'PowerShell session open for 15 minutes', body: 'Scripts you run on this device now start without another key touch.' }); },
    onError: error => toast({ tone: 'warning', title: 'The session didn’t open', body: isStatus(error, 409) ? 'Check you have a security key registered in Settings and that this device runs the newest agent (update it on the Overview tab). A session may also be open already.'
      : isStatus(error, 403) ? 'Opening a session needs permission to run scripts and approve changes on this device.' : describeKeyError(error) }),
  });
  const end = useMutation({ mutationFn: () => endScriptSession(mspId, session.data!.id), onSuccess: refresh });
  if (!canApprove) return null;
  if (left) {
    // Open until it runs out (scripts it approved may still start), but in its last minute, or with its key removed, it
    // approves no new script.
    const approving = session.data!.live;
    return (
      <div className={cn('flex flex-wrap items-center gap-3 border-b border-line px-4 py-2 text-[13px]', approving ? 'bg-healthy-soft/60' : 'bg-sunken/60')}>
        <TimerReset className={cn('size-4', approving ? 'text-healthy' : 'text-ink-3')} />
        <span><span className="font-semibold">PowerShell session open</span> · <span className="tabular font-medium">{left}</span> left · {approving
          ? 'scripts you run here start without another key touch' : 'new scripts need a key touch again'}</span>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" disabled={end.isPending} onClick={() => end.mutate()}>End session</Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-line bg-sunken/40 px-4 py-2 text-[13px] text-ink-2">
      <span className="min-w-0 flex-1">Each script needs your key, or approve all your scripts here for 15 minutes with one touch.</span>
      <Button size="sm" variant="secondary" disabled={!reason.trim() || open.isPending} title={reason.trim() ? undefined : 'Say why first: it is recorded with the session'}
        onClick={() => open.mutate()}><KeyRound className="size-3.5" />{open.isPending ? 'Waiting for your key…' : 'Approve 15 minutes'}</Button>
    </div>
  );
}
